import { motion } from "framer-motion";
import { Phone, MapPin, Mail, Clock, ArrowUp } from "lucide-react";
import { staggerContainer, fadeUp } from "../animations/variants";
import logoImg from "../assests/indur_logo.png.png";

const footerLinks = {
  Services: [
    "CT Scan",
    "MRI Scan",
    "Brain Imaging",
    "Whole Body Scan",
    "Angiography",
    "MR Urography",
  ],
  "Quick Links": ["Home", "About Us", "Technology", "Appointment", "Contact"],
};

export default function Footer() {
  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer className="bg-gray-950 text-gray-300 pt-16 pb-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12"
        >
          {/* Brand */}
          <motion.div variants={fadeUp}>
            <div className="flex items-center gap-3 mb-5">
              <img
                src={logoImg}
                alt="Indur CT & MRI Centre"
                className="h-28 w-auto object-contain"
              />
              <div>
                <div className="font-poppins font-bold text-white text-lg leading-none">
                  INDUR
                </div>
                <div className="text-xs text-primary-400 tracking-wider">
                  CT & MRI CENTRE
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-5">
              Trusted for 30+ years in North Telangana. Advanced imaging,
              accurate results — your health is our mission.
            </p>
            <div className="flex items-center gap-2 text-sm text-primary-400 font-semibold">
              <Clock className="w-4 h-4" />
              24/7 Service Available
            </div>
          </motion.div>

          {/* Links */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <motion.div key={title} variants={fadeUp}>
              <h4 className="font-poppins font-semibold text-white mb-5">
                {title}
              </h4>
              <ul className="space-y-2.5">
                {links.map((link) => (
                  <li key={link}>
                    <button
                      type="button"
                      className="text-sm text-gray-400 hover:text-primary-400 transition-colors flex items-center gap-1.5 group bg-transparent border-0 p-0 cursor-pointer"
                    >
                      <span className="w-1 h-1 rounded-full bg-primary-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                      {link}
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}

          {/* Contact */}
          <motion.div variants={fadeUp}>
            <h4 className="font-poppins font-semibold text-white mb-5">
              Contact Info
            </h4>
            <ul className="space-y-4 text-sm">
              <li className="flex gap-3">
                <MapPin className="w-4 h-4 text-primary-400 shrink-0 mt-0.5" />
                <a
                  href="https://www.google.com/maps?q=18.67553977433319,78.10168465911897"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-primary-400 transition-colors"
                >
                  Door No. 5-7-627 Oppsite of Municipal complex,
                  <br />
                  Rajiv Gandhi Auditorium Road Doctor Line, khaleelwadi.
                </a>
              </li>
              <li className="flex gap-3 items-center">
                <Phone className="w-4 h-4 text-primary-400 shrink-0" />
                <div className="text-gray-400">
                  <a
                    href="tel:+916301195478"
                    className="hover:text-primary-400 transition-colors block"
                  >
                    +91 63011 95478
                  </a>
                  <a
                    href="tel:6301191478"
                    className="hover:text-primary-400 transition-colors block"
                  >
                    63011 91478
                  </a>
                </div>
              </li>
              <li className="flex gap-3 items-center">
                <Mail className="w-4 h-4 text-primary-400 shrink-0" />
                <a
                  href="mailto:indurctmricenter@gmail.com"
                  className="text-gray-400 hover:text-primary-400 transition-colors"
                >
                  indurctmricenter@gmail.com
                </a>
              </li>
            </ul>
          </motion.div>
        </motion.div>

        {/* Bottom bar */}
        <div className="border-t border-gray-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-500 text-center">
            &copy; {new Date().getFullYear()} Indur CT & MRI Centre. All rights
            reserved.
          </p>
          <motion.button
            onClick={scrollTop}
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.9 }}
            className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center text-white shadow-lg hover:bg-primary-600 transition-colors"
            aria-label="Scroll to top"
          >
            <ArrowUp className="w-4 h-4" />
          </motion.button>
        </div>
      </div>
    </footer>
  );
}
